import { BaseApiService } from "@services/baseApiService";
import { api } from "@services/api";
import { uploadFileMultipart } from "@services/multipartUpload";
import {
  FilledFormData,
  TokenValidationResponse,
  TokenResendResponse,
} from "./types";

// Acima deste tamanho, o upload vai por S3 multipart resumável direto do
// navegador (evita gargalo do servidor). Abaixo, segue o caminho atual.
const MULTIPART_THRESHOLD = 8 * 1024 * 1024; // 8 MiB

class FilledFormService extends BaseApiService<FilledFormData> {
  constructor() {
    super("filledforms");
  }

  async create(data: FilledFormData) {
    return api.request<FilledFormData>(`${this.baseUrl}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async validateToken(filledFormId: string, token: string) {
    return api.request<TokenValidationResponse>(
      `${this.baseUrl}/validate-token/`,
      {
        method: "POST",
        headers: api.headers(),
        body: JSON.stringify({ filledFormId, token }),
      }
    );
  }

  async resendToken(filledFormId: string) {
    return api.request<TokenResendResponse>(`${this.baseUrl}/resend-token/`, {
      method: "POST",
      headers: api.headers(),
      body: JSON.stringify({ filledFormId }),
    });
  }

  /**
   * Marca um filledForm como visualizado pelo advogado.
   * Idempotente no BE — chamadas repetidas são ignoradas.
   */
  async markOpened(id: string): Promise<void> {
    await api.request(`${this.baseUrl}/${id}/mark-opened`, {
      method: "PATCH",
      headers: api.headers(),
    });
  }

  /**
   * Marca um filledForm como baixado pelo advogado (primeiro download de anexo).
   * Idempotente no BE — chamadas repetidas são ignoradas.
   */
  async markDownloaded(id: string): Promise<void> {
    await api.request(`${this.baseUrl}/${id}/mark-downloaded`, {
      method: "PATCH",
      headers: api.headers(),
    });
  }

  async submitEmails(payload: {
    emails: string[];
    phaseForm: string;
  }): Promise<any> {
    try {
      const response = await api.request(`${this.baseUrl}/batch-email`, {
        method: "POST",
        headers: api.headers(),
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error("Failed to submit emails");
    }
  }

  async submitPhones(payload: {
    phones: string[];
    phaseFormId: string;
    processId: string;
    productId: string;
  }): Promise<any> {
    try {
      const response = await api.request(`${this.baseUrl}/batch-whatsapp`, {
        method: "POST",
        headers: api.headers(),
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error("Failed to submit phone numbers");
    }
  }

  /**
   * Envia um arquivo para um campo específico de um formulário preenchido
   * @param phaseFormId - ID do formulário de fase
   * @param processId - ID do processo
   * @param formFieldId - ID do campo do formulário
   * @param file - Arquivo a ser enviado
   * @param onProgress - Callback para acompanhar o progresso do upload
   * @returns Resposta da API com o caminho do arquivo
   */
  async uploadDocument(
    phaseFormId: string,
    processId: string,
    formFieldId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    // Validar parâmetros obrigatórios
    if (!phaseFormId) throw new Error("ID do formulário de fase não encontrado. Contate o suporte.");
    if (!processId) throw new Error("ID do processo não encontrado. Contate o suporte.");
    if (!formFieldId) throw new Error("ID do campo não encontrado.");

    // Validar o arquivo
    this.validateFile(file);

    try {
      // Arquivos grandes: upload S3 multipart resumável direto do navegador.
      if (file.size > MULTIPART_THRESHOLD) {
        return await uploadFileMultipart(
          file,
          { phaseFormId, processId, formFieldId },
          onProgress
        );
      }

      // Arquivos pequenos: caminho atual (upload via servidor).
      const formData = new FormData();
      formData.append("file", file);

      const endpoint = `/filledforms/${phaseFormId}/${processId}/${formFieldId}/temp-upload`;

      const response = await api.uploadRequest<any>(
        endpoint,
        formData,
        onProgress
      );

      if ("data" in response) {
        return response.data;
      }

      throw new Error("Resposta inválida do servidor");
    } catch (error) {
      console.error("Erro no upload do documento:", error);
      if (error instanceof Error) {
        const msg = error.message;
        // Distingue tamanho / conexão / servidor quando possível.
        if (/muito grande|tamanho máximo/i.test(msg)) {
          throw new Error(msg);
        }
        if (/conex|internet|tempo limite|network/i.test(msg)) {
          throw new Error(
            `Falha de conexão no upload. Verifique sua internet e tente novamente. (${msg})`
          );
        }
        throw new Error(`Falha no upload: ${msg}`);
      }
      throw error;
    }
  }

  /**
   * Valida o arquivo antes do upload
   * @param file Arquivo a ser validado
   */
  private validateFile(file: File): void {
    // Com o caminho multipart resumável, o teto de 100MB não se aplica.
    // Limite geral elevado para 2 GB.
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

    if (!file || file.size === 0) {
      throw new Error("Nenhum arquivo fornecido");
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error(
        `O arquivo é muito grande. O tamanho máximo permitido é ${
          MAX_FILE_SIZE / (1024 * 1024 * 1024)
        }GB`
      );
    }
  }
}

export const filledFormService = new FilledFormService();
